import { Box, Button, Chip, Container, Grid, Typography } from '@mui/material';
import Image, { type StaticImageData } from 'next/image';
import { useEffect, useRef } from 'react';

import { Link } from '@larvitu/shared/next/mui';

import { SectionStyle } from './SectionStyle';

import type { FC, ImgHTMLAttributes } from 'react';

// -----------------------------------------------------------------------------
// *****************************************************************************
// -----------------------------------------------------------------------------

interface HeroComponentProps {
  greeting: string;
  title: string;
  specialised: string[];
  image: StaticImageData;
  containedButton: string;
  containedLink: string;
  outlinedButton: string | boolean;
}

type NativeImageProps = ImgHTMLAttributes<HTMLImageElement>;

interface ClipShapeProps extends NativeImageProps {
  coordinates: string;
}

const coordinates = '30% 0, 100% 0, 100% 100%, 0% 100%';
const ClipImage: FC<ClipShapeProps> = ({
  coordinates,
  src,
  children,
  ...props
}) => (
  <Box
    sx={{
      background: 'white',
      clipPath: { md: `polygon(${coordinates})` },
      shapeOutside: { md: `polygon(${coordinates})` },
      float: {
        xs: 'unset',
        md: 'right',
      },
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      width: {
        md: 400,
        // lg: 700,
        // xl: 900
      },
    }}
  >
    {children}
  </Box>
);

export const HeroComponent: FC<HeroComponentProps> = ({
  greeting,
  title,
  specialised,
  image,
  containedButton,
  containedLink,
  outlinedButton,
}) => {
  const containedButtonHover = {
    backgroundColor: 'rgba(24, 84, 77, 0.1)',
    border: '1px solid #174741',
    color: 'rgba(24, 84, 77, 1)',
  };
  const mainTitleArray = title.split( ' ' );
  const [word1, word2, word3, word4, word5, ...rest] = mainTitleArray;
  const primaryColor = 'rgba(24, 84, 77, 1)';

  // testing animation play on screen
  const containerRef = useRef<HTMLDivElement>( null );

  useEffect( () => {
    const observer = new IntersectionObserver( (entries) => {
      const [entry] = entries;

      const element = entry.target.querySelector( '#underline' );

      if (entry.isIntersecting) {
        element?.classList.add( 'animationUnderline' );

        return;
      }

      element?.classList.remove( 'animationUnderline' );
    } );

    if (containerRef.current) observer.observe( containerRef.current );
  }, [containerRef] );

  return (
    <>
      <Container
        sx={{
          mb: 8,
        }}
      >
        <Grid container>
          <Grid
            item
            xs={12}
            md={7}
            mt={{ xs: 8, md: 0 }}
            height={{ xs: '100%', md: '550px' }}
            order={{ xs: 2, md: 1 }}
          >
            <Box
              height="100%"
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              ref={containerRef}
            >
              <Box width="100%">
                <Typography variant="h2" component="h1">
                  <Typography
                    variant="body1"
                    component="span"
                    display={{ lg: 'block' }}
                    sx={{ font: 'inherit' }}
                  >
                    {greeting}
                    <br />
                  </Typography>
                  {word1}{'  '}
                  <span style={{ color: primaryColor }}>
                    {word2}
                  </span>
                  <br />
                  <Typography
                    variant="body1"
                    component="span"
                    display={{ lg: 'block' }}
                    sx={{ font: 'inherit' }}
                  >
                    {word3}{' '}
                    <span style={{ color: primaryColor }}>
                      {word4} {' '}
                    </span>
                    <>
                      {word5}
                    </>
                    <br />
                  </Typography>
                  {rest.map( (word, index) => {
                    if (word.includes( 'Berlin' )) {
                      return (
                        <span key={index} className="styledUnderline">
                          {word}&nbsp;
                          <span id="underline">
                          </span>
                        </span>
                      );
                    }

                    return `${word} `;
                  } )}
                </Typography>
                <Box
                  my={4}
                  display="flex"
                  gap={{ xs: 1, lg: 2 }}
                  flexWrap="wrap"
                  width="100%"
                  // width={{ lg: '95%' }}
                >
                  {specialised.map((item, index) => (
                    <Chip key={index} label={item} sx={{ borderRadius: 500 }} />
                  ))}
                </Box>
                <Grid container spacing={2}>
                  <Grid item>
                    <Link href={containedLink}>
                      <Button
                        size="large"
                        sx={{ minWidth: 120, '&:hover': containedButtonHover }}
                        variant="contained"
                      >
                        {containedButton}
                      </Button>
                    </Link>
                  </Grid>
                  <Grid item>
                    <Link href="/services">
                      <Button
                        size="large"
                        sx={{ minWidth: 120 }}
                        variant="outlined"
                      >
                        {outlinedButton}
                      </Button>
                    </Link>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={5}
            order={{ xs: 1, md: 2 }}
          >
            <Box
              position={{
                xs: 'relative',
                md: 'absolute',
              }}
              width={{ xs: '100%', md: '41.66%', lg: 'calc(480px + (100% - 1152px)*0.5  )' }}
              height={{ xs: '200px', sm: '350px', md: '550px' }}
              sx={{
                clipPath: {
                  xs: 'unset',
                  md: 'polygon(20% 0%,100% 0%,100% 100%,0% 100%)',
                  xl: 'polygon(31% 0%,100% 0%,100% 100%,0% 100%)',
                },
              }}
            >
              <Image
                src={image}
                sizes="(max-width: 600px): 100vw,(max-width: 1200px): 90vw, 40vw"
                alt="stockDoc"
                fill
                placeholder="blur"
                priority
                style={{
                  objectFit: 'cover',
                  objectPosition: '50% 30%',
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};
